import {AjaxP, Cookies, DOM, Module} from '@mamdev/cad.static-next-lib';
import Viewport from '../../ts/services/viewport';
import {Url} from '../../ts/services/url';
import {html, render} from 'lit';
import {cities, citiesDesktop} from './templates/cities';
import {
  weatherDetailsDesktop,
  weatherDetailsMobile,
  weatherDetailsTab,
  weatherPagerDetailsMobile
} from './templates/details';

export interface WeatherData {
  cities: CityData[];
  countryCode: string;
  day: number;
  details: WeatherDetails;
  districtId: number;
  loc_weather: string | null;
  region: string;
}

interface WeatherDetails {
  forecasts: Forecast[];
}

export interface Forecast {
  label: string;
  forecastDailyAverage: ForecastDetails;
  forecastEvening: ForecastDetails;
  forecastMorning: ForecastDetails;
  forecastNight: ForecastDetails;
  forecastNoon: ForecastDetails;
  hasDetailedForecasts: boolean;
}

interface ForecastDetails {
  altText: string;
  icon: string;
  minTemperature: number;
  maxTemperature: number;
  precipitation: number;
  temperature: number;
  wind: number;
  windCode: string;
  windDirection: string;
}

export interface CityData {
  city: string;
  encodedName: string;
  forecasts: Forecast[];
  url: string;
}

/**
 * Weather
 */
export default class Weather extends Module {
  daysForCities: {
    // 0 = today, 1 = tmr, 2 = dat
    currentDayIndex: number;
    maxDay: number;
  };

  daysForDetails: {
    currentDayIndex: number;
    maxDay: number;
  };

  cityList: CityData[] | null;
  mapRegion: string | undefined;
  weatherDetails: WeatherDetails | null;

  isCityListPage: boolean;
  isWeatherDetailsPage: boolean;

  cookieData: {
    districtId: number;
    countryCode: string;
    locWeather: string | null;
  };

  detailTmplResults_: unknown[]; // appendChild workaround for lit-html

  selectors: {
    cityList: string;
    cityMap: string;
    details: string;
    weekDays: string;

    pagination: string;
    paginationLeft: string;
    paginationRight: string;

    paginationDetails: string;
    paginationLeftDetails: string;
    paginationRightDetails: string;

    paginationDesktop: string;
    paginationLeftDesktop: string;
    paginationRightDesktop: string;

    cityListPage: string;
    detailsPage: string;

    buttonLocation: string;
    checkMarkPosition: string;
    removePosition: string;

    weatherText: string;

    clickArea: string;
  };

  /**
   * Initialize from DOM
   * @param {HTMLElement} root
   * @return {Weather}
   */
  static attachTo(root: HTMLElement) {
    return new Weather(root);
  }

  /**
   * Con.
   * @param {HTMLElement} root
   */
  constructor(root: HTMLElement) {
    super(root);
    this.name_ = 'weather';

    this.selectors = {
      cityList: '.cities',
      cityMap: '.map-weather',
      details: '.wd',
      weekDays: '.details .wd .week ul li',

      pagination: '.map .pagination.mobile.days .pager.more span',
      paginationLeft: '.map .pagination.mobile.days .pager.icon-arrow-left',
      paginationRight: '.map .pagination.mobile.days .pager.icon-arrow-right',

      paginationDetails: '.details .pagination.mobile.days .pager.more span',
      paginationLeftDetails: '.details .pagination.mobile.days .pager.icon-arrow-left',
      paginationRightDetails: '.details .pagination.mobile.days .pager.icon-arrow-right',

      paginationDesktop: '.map .js-pager',
      paginationLeftDesktop: '.map .pagination.desktop.days .pager.icon-arrow-left',
      paginationRightDesktop: '.map .pagination.desktop.days .pager.icon-arrow-right',

      cityListPage: '.map',
      detailsPage: '.details',

      buttonLocation: '.details .button-center a',
      checkMarkPosition: '.check-position',
      removePosition: '.check-position .text a',

      weatherText: '.wdc .day-weather-text',

      clickArea: '.map .cities ul li'
    };

    this.daysForCities = {
      // 0 = today, 1 = tmr, 2 = dat
      currentDayIndex: 0,
      maxDay: 2
    };

    this.daysForDetails = {
      currentDayIndex: 0,
      // Mo.-So.
      maxDay: 6
    };

    this.cityList = null;
    this.mapRegion = 'de';
    this.weatherDetails = null;

    this.isCityListPage = false;
    this.isWeatherDetailsPage = false;

    this.cookieData = {
      districtId: 0,
      countryCode: '',
      locWeather: ''
    };

    this.detailTmplResults_ = []; // appendChild workaround for lit-html
  }

  /**
   * Start
   */
  start() {
    // weathertool, just stop
    if (this.root_.classList.contains('mod-weathertool')) {
      this.log('Tool view, exit...');
      return;
    }

    // if Cookies doesn't work, the button is invisible
    if (!navigator.cookieEnabled) {
      const locationBtn = this.root_.querySelector<HTMLElement>(this.selectors.buttonLocation);
      if (locationBtn) {
        locationBtn.style.display = 'none';
      }
    }

    this.isCityListPage = this.root_.querySelectorAll(this.selectors.cityListPage).length > 0;
    this.isWeatherDetailsPage = this.root_.querySelectorAll(this.selectors.detailsPage).length > 0;

    this.subscribe('window:resize', () => {
      if (this.isCityListPage) {
        this.updateCityList();
      } else if (this.isWeatherDetailsPage) {
        this.updateWeatherDetails();
      }
    });

    let weatherCitiesUrl = '';
    let weatherDetailsUrl = '';

    if (this.isCityListPage) {
      const cityListPage = this.root_.querySelector(this.selectors.cityListPage);
      weatherCitiesUrl = cityListPage?.getAttribute('data-url') ?? '';
      this.mapRegion = cityListPage?.getAttribute('data-region') ?? undefined;

      if (weatherCitiesUrl) {
        void AjaxP.getJSON(weatherCitiesUrl).then(data => {
          this.cityList = (data as WeatherData).cities;

          // initial weather (today)
          this.updateCityList();

          // events for pager...
          this.root_.querySelector(this.selectors.paginationRight)?.addEventListener('click', () => {
            this.next();
          });
          this.root_.querySelector(this.selectors.paginationLeft)?.addEventListener('click', () => {
            this.prev();
          });

          const dayTabs = this.root_.querySelectorAll(this.selectors.paginationDesktop);
          DOM.forEach(dayTabs, (dt, idx) => {
            if (!idx) return;

            dt.addEventListener('click', () => {
              this.daysForCities.currentDayIndex = idx;
              this.updateCityList();
              this.updatePagination();
            });
          });
        });
      }
    } else if (this.isWeatherDetailsPage) {
      const detailsPageElement = this.root_.querySelector(this.selectors.detailsPage);

      weatherDetailsUrl = detailsPageElement?.getAttribute('data-url') ?? '';
      if (weatherDetailsUrl) {
        void AjaxP.getJSON(weatherDetailsUrl).then(data => {
          this.weatherDetails = data as WeatherDetails;

          this.cookieData.districtId = (data as WeatherData).districtId || 11;
          this.cookieData.countryCode = (data as WeatherData).countryCode || 'de';
          this.cookieData.locWeather = (data as WeatherData).loc_weather ?? null; // CADNPCA-6262

          const tabData = {
            day: this.daysForDetails.currentDayIndex,
            details: this.weatherDetails
          } as WeatherData;

          // render tab for mobile (pager-like)
          const tabHtmlMobile = weatherPagerDetailsMobile(tabData);
          // render(tabHtmlMobile, this.root_.querySelector(this.selectors.details));
          // render the tabs
          const tabHtmlDesktop = weatherDetailsTab(tabData);
          // was using appendChild to combine two HTML parts
          // render(html`${[tabHtmlMobile, tabHtmlDesktop]}`, this.root_.querySelector(this.selectors.details));
          this.detailTmplResults_.push(tabHtmlMobile, tabHtmlDesktop, '');

          this.updateWeatherDetails();

          const weatherText = this.root_.querySelectorAll<HTMLElement>(this.selectors.weatherText);
          weatherText[this.daysForDetails.currentDayIndex].style.display = 'block';

          // events for pager...
          this.root_.querySelector(this.selectors.paginationRightDetails)?.addEventListener('click', () => {
            this.nextDetails();
          });
          this.root_.querySelector(this.selectors.paginationLeftDetails)?.addEventListener('click', () => {
            this.prevDetails();
          });

          // events for tabs
          const weekTabs = this.root_.querySelectorAll(this.selectors.weekDays);
          DOM.forEach(weekTabs, (wt, idx) => {
            wt.addEventListener('click', () => {
              this.daysForDetails.currentDayIndex = idx ?? 0;
              this.updatePaginationDetails();
              this.updateWeatherDetails();
            });
          });
        });
      }
    }

    const button = this.root_.querySelector<HTMLElement>(this.selectors.buttonLocation);
    const checkMark = this.root_.querySelector<HTMLElement>(this.selectors.checkMarkPosition);

    if (button) {
      button.addEventListener('click', evt => {
        evt.preventDefault();
        try {
          // set cookie
          if (this.cookieData.locWeather) {
            Cookies.setItem('loc_weather', this.cookieData.locWeather, 60 * 60 * 24 * 365, '/',
              `.${Url.getBaseDomain()}`);
          } else {
            Cookies.setItem('weather_districtId', this.cookieData.districtId.toString(), 60 * 60 * 24 * 365, '/');
            Cookies.setItem('weather_countryCode', this.cookieData.countryCode, 60 * 60 * 24 * 365, '/');
          }
          button.style.display = 'none';
          if (checkMark) checkMark.style.display = 'block';
        } catch (e) {
          this.logUnknownError(e);
        }
      });
    }

    const rmPos = this.root_.querySelector(this.selectors.removePosition);

    if (rmPos) {
      rmPos.addEventListener('click', evt => {
        evt.preventDefault();
        try {
          // remove cookie
          Cookies.removeItem('weather_districtId', '/');
          Cookies.removeItem('weather_countryCode', '/');
          Cookies.removeItem('loc_weather', '/', `.${Url.getBaseDomain()}`);

          if (button) button.style.display = 'inline-block';
          if (checkMark) checkMark.style.display = 'none';
        } catch (e) {
          this.logUnknownError(e);
        }
      });
    }
  }

  /**
   * Renders city list
   */
  updateCityList() {
    const renderParam = {
      day: this.daysForCities.currentDayIndex,
      cities: this.cityList,
      region: this.mapRegion
    } as WeatherData;

    if (Viewport.matches('tablet')) {
      const weatherSkeletonPlaceholder = document.getElementById('weather-desktop-view-placeholder');
      const cityListHtmlDesktop = citiesDesktop(renderParam);
      const cityMap = this.root_.querySelector<HTMLElement>(this.selectors.cityMap);
      if (this.mapRegion) {
        cityMap?.classList.add(this.mapRegion);
      }
      if (weatherSkeletonPlaceholder) {
        weatherSkeletonPlaceholder.style.display = 'none';
      }
      if (cityMap) {
        render(cityListHtmlDesktop, cityMap);
      }
    } else {
      const weatherSkeletonPlaceholder = document.getElementById('weather-mobile-view-placeholder');
      const cityListHtml = cities(renderParam);
      const cityList = this.root_.querySelector<HTMLElement>(this.selectors.cityList);
      // disable placeholder
      if (weatherSkeletonPlaceholder) {
        weatherSkeletonPlaceholder.style.display = 'none';
      }
      if (cityList) {
        render(cityListHtml, cityList);
      }
      // all rows in mobile are clickable
      this.applyClickCity();
    }
  }

  /**
   * Previous day
   */
  prev() {
    if (this.daysForCities.currentDayIndex - 1 >= 0) {
      this.daysForCities.currentDayIndex--;
    }
    this.updateCityList();
    this.updatePagination();
  }

  /**
   * Next day
   */
  next() {
    if (this.daysForCities.currentDayIndex + 1 <= this.daysForCities.maxDay) {
      this.daysForCities.currentDayIndex++;
    }
    this.updateCityList();
    this.updatePagination();
  }

  /**
   * Updates pager status
   */
  updatePagination() {
    // mobile pager (today, tomorrow...)
    DOM.removeClass(this.root_.querySelectorAll(this.selectors.paginationLeft
      + ', ' + this.selectors.paginationRight), 'inactive');

    if (this.daysForCities.currentDayIndex === 0) {
      this.root_.querySelector(this.selectors.paginationLeft)?.classList.add('inactive');
    }

    if ((this.daysForCities.currentDayIndex === this.daysForCities.maxDay)) {
      this.root_.querySelector(this.selectors.paginationRight)?.classList.add('inactive');
    }

    // hide all day labels
    DOM.addClass(this.root_.querySelectorAll(this.selectors.pagination), 'inactive');
    // then show the right one
    DOM.removeClass(this.root_.querySelectorAll(this.selectors.pagination)[this.daysForCities.currentDayIndex], 'inactive');

    // hide all day labels
    DOM.removeClass(this.root_.querySelectorAll(this.selectors.paginationDesktop), 'tabnavi__item_current');
    // then show the right one
    DOM.addClass(this.root_.querySelectorAll(this.selectors.paginationDesktop)[this.daysForCities.currentDayIndex], 'tabnavi__item_current');
  }

  /**
   * Renders weather details
   */
  updateWeatherDetails() {
    const weatherSkeletonPlaceholder = this.root_.querySelector<HTMLElement>('.wd-placeholder');
    const wd = this.root_.querySelector<HTMLElement>(this.selectors.details);
    const wdData = {
      day: this.daysForDetails.currentDayIndex,
      details: this.weatherDetails
    } as WeatherData;

    this.detailTmplResults_.pop();

    if (Viewport.matches('tablet')) {
      const detailsHtmlDesk = weatherDetailsDesktop(wdData);
      this.detailTmplResults_.push(detailsHtmlDesk);
    } else {
      const detailsHtml = weatherDetailsMobile(wdData);
      this.detailTmplResults_.push(detailsHtml);
    }
    // disable placeholder
    if (weatherSkeletonPlaceholder) {
      weatherSkeletonPlaceholder.style.display = 'none';
    }
    if (wd) render(html`${this.detailTmplResults_}`, wd);
  }

  /**
   * Previous day detail
   */
  prevDetails() {
    if ((this.daysForDetails.currentDayIndex - 1) >= 0) {
      this.daysForDetails.currentDayIndex--;
    }
    this.updateWeatherDetails();
    this.updatePaginationDetails();
  }

  /**
   * Next day detail
   */
  nextDetails() {
    if ((this.daysForDetails.currentDayIndex + 1) <= this.daysForDetails.maxDay) {
      this.daysForDetails.currentDayIndex++;
    }
    this.updateWeatherDetails();
    this.updatePaginationDetails();
  }

  /**
   * Pager on details page
   */
  updatePaginationDetails() {
    const weatherText = this.root_.querySelectorAll<HTMLElement>(this.selectors.weatherText);
    DOM.forEach(weatherText, wt => {
      (wt as HTMLElement).style.display = 'none';
    });
    weatherText[this.daysForDetails.currentDayIndex].style.display = 'block';

    // mobile
    DOM.removeClass(this.root_.querySelectorAll(this.selectors.paginationLeftDetails + ', ' + this.selectors.paginationRightDetails), 'inactive');

    if (this.daysForDetails.currentDayIndex === 0) {
      this.root_.querySelector(this.selectors.paginationLeftDetails)?.classList.add('inactive');
    }

    if (this.daysForDetails.currentDayIndex === this.daysForDetails.maxDay) {
      this.root_.querySelector(this.selectors.paginationRightDetails)?.classList.add('inactive');
    }

    // hide all day labels
    DOM.addClass(this.root_.querySelectorAll(this.selectors.paginationDetails), 'inactive');
    // then show the right one
    DOM.removeClass(this.root_.querySelectorAll(this.selectors.paginationDetails)[this.daysForDetails.currentDayIndex], 'inactive');

    // Desktop (tab)
    const weekTabs = this.root_.querySelectorAll(this.selectors.weekDays);
    DOM.removeClass(weekTabs, 'active');
    DOM.addClass(weekTabs[this.daysForDetails.currentDayIndex], 'active');
  }

  /**
   * Whole area clickable
   */
  applyClickCity() {
    const targets = this.root_.querySelectorAll(this.selectors.clickArea);

    DOM.forEach(targets, t => {
      t.addEventListener('click', () => {
        const link = t.getElementsByTagName('a')[0];
        location.href = link.href;
      });
    });
  }
}
